import type { ReactNode } from "react";

import { cn } from "../lib/utils";

/**
 * Tones step down in visual weight so they read apart by lightness, not just
 * hue: `strong` is a solid fill, `muted` barely more than text.
 */
export type BadgeTone = "strong" | "medium" | "warning" | "neutral" | "muted";

const TONE_STYLES: Record<BadgeTone, string> = {
  strong: "bg-amber-700 text-white ring-amber-700",
  medium: "bg-amber-200 text-amber-900 ring-amber-700/30",
  warning: "bg-amber-50 text-amber-800 ring-amber-600/30",
  neutral: "bg-gray-100 text-gray-800 ring-gray-600/20",
  muted: "bg-white text-gray-500 ring-gray-300",
};

/**
 * A small pill for free-form labels. Same shape as `StatusV2`, which is tied
 * to job statuses; use this for anything else. Never wraps.
 */
export function Badge({
  tone = "neutral",
  className,
  children,
}: {
  tone?: BadgeTone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset",
        TONE_STYLES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
